/**
 * XC-AI-013 — useHrAiOptIn
 *
 * Returns the current opt-in state for the active user and helpers to
 * open/close the disclaimer modal. Components that gate AI HR tools
 * import this and render <HrToolOptIn /> when `needsOptIn` is true and
 * `modalOpen` is true.
 *
 * Usage example:
 *
 *   const { hasOptedIn, modalOpen, openModal, closeModal, refresh } = useHrAiOptIn();
 *
 *   const handleAskHrAi = () => {
 *     if (!hasOptedIn) { openModal(); return; }
 *     // ... proceed with the AI HR tool
 *   };
 */

import { useState, useCallback } from 'react';
import { useAuth } from '@/lib/AuthContext';

export function useHrAiOptIn() {
  const { user, refreshAuth } = useAuth() ?? {};
  const [modalOpen, setModalOpen] = useState(false);

  const hasOptedIn = Boolean(user?.ai_hr_optin);

  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  const refresh = useCallback(async () => {
    if (typeof refreshAuth === 'function') {
      await refreshAuth();
    }
  }, [refreshAuth]);

  return {
    hasOptedIn,
    needsOptIn: !hasOptedIn,
    modalOpen,
    openModal,
    closeModal,
    refresh,
  };
}

/**
 * Detect whether a tool result returned by the AI orchestrator is the
 * structured opt-in refusal payload. The handler returns this payload
 * with `code: 'AI_HR_OPTIN_REQUIRED'` when the user hasn't accepted yet.
 */
export function isHrOptInRefusal(result) {
  if (!result || typeof result !== 'object') return false;
  return result.code === 'AI_HR_OPTIN_REQUIRED';
}
